import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Security, ContentCopy } from '@mui/icons-material';
import CryptoJS from 'crypto-js';

function HashGenerator() {
  const [inputText, setInputText] = useState('');
  const [hashType, setHashType] = useState('all');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const generateHashes = () => {
    if (!inputText.trim()) {
      setError('Please enter text to hash');
      return;
    }

    setError(null);

    try {
      const hashes = {};

      if (hashType === 'all' || hashType === 'md5') {
        hashes.md5 = CryptoJS.MD5(inputText).toString();
      }
      
      if (hashType === 'all' || hashType === 'sha1') {
        hashes.sha1 = CryptoJS.SHA1(inputText).toString();
      }
      
      if (hashType === 'all' || hashType === 'sha256') {
        hashes.sha256 = CryptoJS.SHA256(inputText).toString();
      }
      
      if (hashType === 'all' || hashType === 'sha512') {
        hashes.sha512 = CryptoJS.SHA512(inputText).toString();
      }
      
      if (hashType === 'all' || hashType === 'sha3') {
        hashes.sha3 = CryptoJS.SHA3(inputText).toString();
      }

      // Additional hash types
      if (hashType === 'all' || hashType === 'ripemd160') {
        hashes.ripemd160 = CryptoJS.RIPEMD160(inputText).toString();
      }

      // Base64 encoding
      if (hashType === 'all' || hashType === 'base64') {
        hashes.base64 = CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(inputText));
      }

      setResults({
        originalText: inputText,
        hashes: hashes,
        timestamp: new Date().toLocaleString()
      });
    } catch (err) {
      setError('Error generating hashes. Please try again.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getHashInfo = (type) => {
    const info = {
      md5: { name: 'MD5', bits: '128-bit', description: 'Fast but cryptographically broken' },
      sha1: { name: 'SHA-1', bits: '160-bit', description: 'Deprecated for security applications' },
      sha256: { name: 'SHA-256', bits: '256-bit', description: 'Secure and widely used' },
      sha512: { name: 'SHA-512', bits: '512-bit', description: 'More secure, larger output' },
      sha3: { name: 'SHA-3', bits: '224-bit', description: 'Latest SHA standard' },
      ripemd160: { name: 'RIPEMD-160', bits: '160-bit', description: 'Alternative to SHA-1' },
      base64: { name: 'Base64', bits: 'Encoding', description: 'Not a hash, but encoding' }
    };
    return info[type] || { name: type.toUpperCase(), bits: 'Unknown', description: 'Hash function' };
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Hash Generator
      </Typography>
      <Card sx={{ 
        p: 0,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Text to Hash"
              variant="outlined"
              multiline
              rows={4}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Enter text, password, or data to generate hashes..."
            />

            <FormControl fullWidth>
              <InputLabel>Hash Type</InputLabel>
              <Select
                value={hashType}
                label="Hash Type"
                onChange={(e) => setHashType(e.target.value)}
              >
                <MenuItem value="all">All Hash Types</MenuItem>
                <MenuItem value="md5">MD5</MenuItem>
                <MenuItem value="sha1">SHA-1</MenuItem>
                <MenuItem value="sha256">SHA-256</MenuItem>
                <MenuItem value="sha512">SHA-512</MenuItem>
                <MenuItem value="sha3">SHA-3</MenuItem>
                <MenuItem value="ripemd160">RIPEMD-160</MenuItem>
                <MenuItem value="base64">Base64 Encoding</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              color="primary"
              onClick={generateHashes}
              startIcon={<Security />}
              sx={{ mt: 2 }}
            >
              Generate Hashes
            </Button>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {results && (
              <Card sx={{ 
                mt: 2, 
                p: 3, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: 2
              }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Generated Hashes
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Original Text:</strong> "{results.originalText.substring(0, 100)}{results.originalText.length > 100 ? '...' : ''}"
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Generated: {results.timestamp}
                  </Typography>
                </Alert>
                
                <TableContainer component={Paper} sx={{ bgcolor: 'background.default' }}>
                  <Table>
                    <TableBody>
                      {Object.entries(results.hashes).map(([type, hash]) => {
                        const hashInfo = getHashInfo(type);
                        return (
                          <TableRow key={type}>
                            <TableCell sx={{ width: '150px' }}>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {hashInfo.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {hashInfo.bits}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: 'monospace', 
                                    wordBreak: 'break-all',
                                    flex: 1,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {hash}
                                </Typography>
                                <Tooltip title="Copy to clipboard">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => copyToClipboard(hash)}
                                    sx={{ ml: 1 }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {hashInfo.description}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Security Note:</strong> MD5 and SHA-1 are cryptographically broken and should not be used for security purposes. 
                    Use SHA-256 or higher for secure applications.
                  </Typography>
                </Alert>
              </Card>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default HashGenerator;
